import { EmptyState } from '@/components/ui/empty-state';
import { Card } from '@/components/ui/card';
import { CalendarBadge } from '@/components/ui/calendar-badge';
import { MiniGauge } from '@/components/ui/mini-gauge';
import { router } from '@inertiajs/react';
import { ChevronRight, Sparkles } from 'lucide-react';
import type { JournalEntry } from '@domain/journal';
import { getCalendarBadgeParts } from '@/lib/date';

type Props = {
  entries: JournalEntry[];
};

type JournalStatus = 'completed' | 'in_progress' | 'not_started';

const deriveStatus = (entry: JournalEntry): JournalStatus => {
  if (entry.completedAt || entry.currentStep === 'completed') return 'completed';
  if (entry.currentStep) return 'in_progress';
  return 'not_started';
};

export default function JournalEntryList({ entries }: Props) {
  const completedEntries = entries.filter((entry) => deriveStatus(entry) === 'completed');

  const handleEntryClick = (entry: JournalEntry) => {
    if (deriveStatus(entry) === 'completed') {
      router.get(`/patients/journals/${entry.id}`);
    }
  };

  if (completedEntries.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState
          title="Nessun diario completato ancora disponibile."
          description="Quando chiudi un diario di giornata, troverai qui il riepilogo 5F."
          icon={<Sparkles className="h-8 w-8" aria-hidden />}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {completedEntries.map((entry) => {
        const moodLevel = entry.warmup?.moodLevel ?? null;
        const energyLevel = entry.warmup?.energyLevel ?? null;
        const gauges: Array<{ label: string; value: number | null }> = [
          { label: 'Umore', value: moodLevel },
          { label: 'Energia', value: energyLevel },
        ];
        const badge = getCalendarBadgeParts(entry.date);

        return (
          <Card
            key={entry.id}
            className="p-4 transition-colors hover:bg-muted/50 cursor-pointer"
            onClick={() => handleEntryClick(entry)}
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-4">
                <CalendarBadge day={badge?.day ?? '--'} month={badge?.month ?? '—'} />
                <small className="text-muted-foreground">Completato</small>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {gauges.map(({ label, value }) =>
                  typeof value === 'number' ? (
                    <MiniGauge key={label} value={value} label={label} />
                  ) : null,
                )}
              </div>

              <div className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
